import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Reupholster Ottoman — per Quoting_markdown/reupholster_ottomans.md.
// Reupholstering an existing customer-owned ottoman, priced per square foot
// of the top (with a $350 minimum) rather than per inch of width like
// Reupholster Bench — but shares that type's Welt/Trim/Nailheads/Buttons/
// Skirt options and fabric-yardage cut formulas almost verbatim. Tufted and
// Attached Pillow Top are independent, stackable toggles here (confirmed
// with the shop), not a mutually exclusive style radio. Submitted through
// dedicated addReupholsterOttomanDraftQuoteObject/
// updateReupholsterOttomanDraftQuoteObject mutations, same deviation
// Bench/Cornice/Bed/Reupholster Bench already established.
const ReupholsterOttomanForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  const [shape, setShape] = useState<string>(iv?.ottomanShape || '');
  const [width, setWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [depth, setDepth] = useState(iv?.depthInches != null ? String(iv.depthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidth, setFabricWidth] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeat, setPatternRepeat] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');
  const [attachedPillowTop, setAttachedPillowTop] = useState<boolean>(iv?.attachedPillowTop || false);
  const [tufted, setTufted] = useState<boolean>(iv?.tufted || false);
  const [baseballStitching, setBaseballStitching] = useState<boolean>(iv?.baseballStitching || false);
  const [weltTop, setWeltTop] = useState<boolean>(iv?.weltTop || false);
  const [weltBottom, setWeltBottom] = useState<boolean>(iv?.weltBottom || false);
  const [trimTop, setTrimTop] = useState<boolean>(iv?.trimTop || false);
  const [trimBottom, setTrimBottom] = useState<boolean>(iv?.trimBottom || false);
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [nailheadTop, setNailheadTop] = useState<boolean>(iv?.nailheadTop || false);
  const [nailheadBottom, setNailheadBottom] = useState<boolean>(iv?.nailheadBottom || false);
  const [nailheadColor, setNailheadColor] = useState<string>(iv?.nailheadColor || '');
  const [buttons, setButtons] = useState<boolean>(iv?.buttons || false);
  const [buttonCount, setButtonCount] = useState(iv?.buttonCount != null ? String(iv.buttonCount) : '');
  const [skirtType, setSkirtType] = useState<string>(iv?.reupholsterSkirtType || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  const isRound = shape === 'round';

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!shape) { setError('Shape is required'); return null; }
      if (!width) { setError(isRound ? 'Diameter is required' : 'Width is required'); return null; }
      if (!isRound && !depth) { setError('Depth is required'); return null; }
      if (!height) { setError('Height is required'); return null; }
      setError(null);
      return {
        ottomanShape: shape,
        widthInches: parseFloat(width),
        depthInches: isRound ? parseFloat(width) : parseFloat(depth),
        heightInches: parseFloat(height),
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidth ? parseFloat(fabricWidth) : null,
        patternRepeatInches: patternRepeat ? parseFloat(patternRepeat) : null,
        attachedPillowTop,
        tufted,
        baseballStitching,
        weltTop,
        weltBottom,
        trimTop,
        trimBottom,
        trimDescription: (trimTop || trimBottom) ? (trimDescription.trim() || null) : null,
        nailheadTop,
        nailheadBottom,
        nailheadColor: (nailheadTop || nailheadBottom) ? (nailheadColor.trim() || null) : null,
        buttons,
        buttonCount: buttons && buttonCount ? parseInt(buttonCount, 10) : null,
        reupholsterSkirtType: skirtType || null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Shape</label>
          <select
            className="input"
            value={shape}
            onChange={(e) => setShape(e.target.value)}
            disabled={props.disabled}
          >
            <option value="">Select...</option>
            <option value="round">Round</option>
            <option value="square_rectangular">Square/Rectangular</option>
          </select>
        </div>
      </div>

      {isRound ? (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Diameter (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={width}
              onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Height (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={height}
              onChange={(e) => setHeight(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      ) : (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Width (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={width}
              onChange={(e) => setWidth(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Depth (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={depth}
              onChange={(e) => setDepth(e.target.value)} disabled={props.disabled} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Height (inches)</label>
            <input type="number" min="0" step="0.25" className="input" value={height}
              onChange={(e) => setHeight(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidth}
            onChange={(e) => setFabricWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeat}
            onChange={(e) => setPatternRepeat(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={attachedPillowTop}
              onChange={(e) => setAttachedPillowTop(e.target.checked)} disabled={props.disabled} />
            {' '}Attached pillow top (+$250 flat)
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={tufted}
              onChange={(e) => setTufted(e.target.checked)} disabled={props.disabled} />
            {' '}Tufted (+$250 if Width+Depth ≤60", +$500 if &gt;60")
          </label>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={baseballStitching}
              onChange={(e) => setBaseballStitching(e.target.checked)} disabled={props.disabled} />
            {' '}Baseball stitching (+$100 flat)
          </label>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={weltTop}
              onChange={(e) => setWeltTop(e.target.checked)} disabled={props.disabled} />
            {' '}Welt on top
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={weltBottom}
              onChange={(e) => setWeltBottom(e.target.checked)} disabled={props.disabled} />
            {' '}Welt on bottom
          </label>
        </div>
        <div className="form-group" style={{ flex: 1, color: '#9ca3af', fontSize: 13, alignSelf: 'center' }}>
          {(weltTop || weltBottom) && 'Flat $36 labor + 1 additional yard of fabric, regardless of placement'}
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={trimTop}
              onChange={(e) => setTrimTop(e.target.checked)} disabled={props.disabled} />
            {' '}Trim around top
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={trimBottom}
              onChange={(e) => setTrimBottom(e.target.checked)} disabled={props.disabled} />
            {' '}Trim around bottom
          </label>
        </div>
        {(trimTop || trimBottom) && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Trim Name <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shared by top/bottom)</span></label>
            <input type="text" className="input" value={trimDescription}
              onChange={(e) => setTrimDescription(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={nailheadTop}
              onChange={(e) => setNailheadTop(e.target.checked)} disabled={props.disabled} />
            {' '}Nailheads around top
          </label>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={nailheadBottom}
              onChange={(e) => setNailheadBottom(e.target.checked)} disabled={props.disabled} />
            {' '}Nailheads around bottom
          </label>
        </div>
        {(nailheadTop || nailheadBottom) && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>Nailhead Color</label>
            <input type="text" className="input" value={nailheadColor}
              onChange={(e) => setNailheadColor(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>
            <input type="checkbox" checked={buttons}
              onChange={(e) => setButtons(e.target.checked)} disabled={props.disabled} />
            {' '}Buttons (+$10 each)
          </label>
        </div>
        {buttons && (
          <div className="form-group" style={{ width: 140 }}>
            <label>Number of Buttons</label>
            <input type="number" min="0" step="1" className="input" value={buttonCount}
              onChange={(e) => setButtonCount(e.target.value)} disabled={props.disabled} />
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Skirt</label>
          <select
            className="input"
            value={skirtType}
            onChange={(e) => setSkirtType(e.target.value)}
            disabled={props.disabled}
          >
            <option value="">None</option>
            <option value="pleated">Pleated ($200 flat)</option>
            <option value="gathered">Gathered ($200 flat)</option>
          </select>
        </div>
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default ReupholsterOttomanForm;
